import { notification, Popover } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import APIClient from '../api/api';
import {
  ListNCAftermarketBidsResponse,
  ListNCAftermarketSalesRequest,
  ListNCAftermarketSalesResponse,
  NCAftermarketBid,
  NCAftermarketSale,
} from '../api/models';
import moment from 'moment';
import { debounce } from 'lodash';
import { Button, Column, DataTable, DomainName, FilterPill, Num, StatusTag, TabItem, Tabs } from '../components/ui';
import { PageFooter, PageHead, PageToolbar } from '../layout';

const DEFAULT_PAGE_SIZE = 25;

// Dropdown options for "ending within" — values are window sizes in seconds.
const ENDING_WITHIN_OPTIONS: { label: string; value: string }[] = [
  { label: 'Next 1 hour', value: '3600' },
  { label: 'Next 6 hours', value: '21600' },
  { label: 'Next 24 hours', value: '86400' },
  { label: 'Next 3 days', value: '259200' },
  { label: 'Next 7 days', value: '604800' },
  { label: 'Next 30 days', value: '2592000' },
  { label: 'Any time', value: '' },
];
const DEFAULT_ENDING_WITHIN = '604800'; // 7 days
const URGENT_THRESHOLD_MS = 60 * 60 * 1000; // < 1h = urgent row

function endingWindowLabel(value: string): string {
  return ENDING_WITHIN_OPTIONS.find((o) => o.value === value)?.label.replace('Next ', '') ?? '—';
}

function buildEndDateRange(windowSeconds: string): string | undefined {
  if (!windowSeconds) return undefined;
  const window = parseInt(windowSeconds, 10);
  if (!Number.isFinite(window) || window <= 0) return undefined;
  const nowSec = Math.floor(Date.now() / 1000);
  return `${nowSec}_${nowSec + window}`;
}

// Convert "100-1000" → "100_1000".
function toRange(input?: string): string | undefined {
  if (!input) return undefined;
  const trimmed = input.trim();
  if (!trimmed) return undefined;
  return trimmed.replace(/-/g, '_');
}

interface FilterState {
  endingWithin: string;
  name?: string;
  tld?: string;
  keywords?: string;
  price?: string;
  nameLength?: string;
  bidCount?: string;
  noHyphens?: boolean;
  noNumbers?: boolean;
}

const defaultFilters: FilterState = { endingWithin: DEFAULT_ENDING_WITHIN };

function buildRequest(f: FilterState): ListNCAftermarketSalesRequest {
  return {
    pageSize: DEFAULT_PAGE_SIZE,
    orderBy: 'end_time',
    direction: 'asc',
    endDate: buildEndDateRange(f.endingWithin),
    name: f.name || undefined,
    tld: f.tld || undefined,
    keywords: f.keywords || undefined,
    price: toRange(f.price),
    nameLength: toRange(f.nameLength),
    bidCount: toRange(f.bidCount),
    noHyphens: f.noHyphens || undefined,
    noNumbers: f.noNumbers || undefined,
  };
}

const NCAftermarket = () => {
  const [activeTab, setActiveTab] = useState<'auctions' | 'bids'>('auctions');
  const [auctions, setAuctions] = useState<ListNCAftermarketSalesResponse>();
  const [auctionsLoading, setAuctionsLoading] = useState(false);
  const [bids, setBids] = useState<ListNCAftermarketBidsResponse>();
  const [bidsLoading, setBidsLoading] = useState(false);
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [bidsPage, setBidsPage] = useState(1);

  // Initial load.
  useEffect(() => {
    loadAuctions(buildRequest(filters));
    loadBids(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadAuctions(req: ListNCAftermarketSalesRequest) {
    setAuctionsLoading(true);
    try {
      setAuctions(await APIClient.listNCAftermarketSales(req));
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to load auctions.' });
    } finally {
      setAuctionsLoading(false);
    }
  }

  async function loadBids(page: number) {
    setBidsLoading(true);
    try {
      const resp = await APIClient.listNCAftermarketBids({ page, pageSize: DEFAULT_PAGE_SIZE });
      setBids(resp);
      setBidsPage(resp.pages?.page || page);
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to load bids.' });
    } finally {
      setBidsLoading(false);
    }
  }

  // Debounce text input changes; fire selects/switches immediately.
  const reload = useMemo(
    () => debounce((next: FilterState) => loadAuctions(buildRequest(next)), 350),
    [],
  );

  function updateFilter<K extends keyof FilterState>(key: K, value: FilterState[K], immediate = false) {
    const next = { ...filters, [key]: value };
    setFilters(next);
    setCursorStack([]);
    if (immediate) {
      reload.cancel();
      loadAuctions(buildRequest(next));
    } else {
      reload(next);
    }
  }

  function clearFilters() {
    setFilters(defaultFilters);
    setCursorStack([]);
    loadAuctions(buildRequest(defaultFilters));
  }

  function nextPage() {
    if (!auctions?.nextCursor) return;
    const newStack = [...cursorStack, auctions.nextCursor];
    setCursorStack(newStack);
    loadAuctions({ ...buildRequest(filters), cursor: auctions.nextCursor });
  }

  function prevPage() {
    if (cursorStack.length === 0) return;
    const newStack = cursorStack.slice(0, -1);
    setCursorStack(newStack);
    const cursor = newStack.length > 0 ? newStack[newStack.length - 1] : undefined;
    loadAuctions({ ...buildRequest(filters), cursor });
  }

  const activeFilterCount =
    [filters.name, filters.tld, filters.keywords, filters.price, filters.nameLength, filters.bidCount]
      .filter((v) => v && v.toString().trim().length > 0).length +
    (filters.noHyphens ? 1 : 0) +
    (filters.noNumbers ? 1 : 0);

  const tabs: TabItem[] = [
    { key: 'auctions', label: 'Live', count: auctions?.items.length },
    { key: 'bids', label: 'My Bids', count: bids?.pages?.total ?? bids?.items.length },
  ];

  return (
    <>
      <PageHead
        title="NC Aftermarket"
        subtitle="Live aftermarket inventory across Namecheap auctions and buy-now listings."
        /* TODO: add Export and + New bid action buttons */
      />

      <PageToolbar>
        <Tabs items={tabs} activeKey={activeTab} onChange={(k) => setActiveTab(k as typeof activeTab)} separator />
        {activeTab === 'auctions' && (
          <>
            <FilterPopover
              label="Ending"
              value={endingWindowLabel(filters.endingWithin)}
              active={filters.endingWithin !== DEFAULT_ENDING_WITHIN}
              content={(close) => (
                <div className="flex flex-col min-w-[160px]">
                  {ENDING_WITHIN_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        updateFilter('endingWithin', opt.value, true);
                        close();
                      }}
                      className={`text-left px-2 py-1.5 text-[12.5px] rounded hover:bg-bg-elevated ${
                        filters.endingWithin === opt.value ? 'text-accent font-semibold' : 'text-text-2'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            />
            <FilterTextPopover
              label="Name"
              value={filters.name}
              placeholder="example"
              onChange={(v) => updateFilter('name', v)}
            />
            <FilterTextPopover
              label="TLD"
              value={filters.tld}
              placeholder="com,io"
              onChange={(v) => updateFilter('tld', v)}
            />
            <FilterTextPopover
              label="Price"
              value={filters.price}
              placeholder="100-5000"
              onChange={(v) => updateFilter('price', v)}
            />
            <FilterTextPopover
              label="Length"
              value={filters.nameLength}
              placeholder="3-6"
              onChange={(v) => updateFilter('nameLength', v)}
            />
            <FilterTextPopover
              label="Bids"
              value={filters.bidCount}
              placeholder="1-"
              onChange={(v) => updateFilter('bidCount', v)}
            />
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-[11px] text-text-3 hover:text-text font-mono px-1"
              >
                clear ({activeFilterCount})
              </button>
            )}
          </>
        )}
      </PageToolbar>

      {activeTab === 'auctions' ? (
        <>
          <DataTable
            columns={auctionColumns}
            data={auctions?.items ?? []}
            rowKey={(s, i) => s.id || s.name || String(i)}
            loading={auctionsLoading}
            rowClassName={(s) => isUrgent(s) ? 'bg-danger-soft/30 hover:bg-danger-soft/50' : ''}
            emptyMessage="No live auctions match these filters."
          />
          <PageFooter
            left={
              <span>
                Showing <b className="text-text-2">{auctions?.items.length ?? 0}</b> · refresh <b className="text-text-2">manual</b> ·
                order <b className="text-text-2">end_time asc</b>
              </span>
            }
            right={
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={prevPage}
                  disabled={cursorStack.length === 0 || auctionsLoading}
                  className="hover:text-text disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  ← prev
                </button>
                <button
                  type="button"
                  onClick={nextPage}
                  disabled={!auctions?.hasMore || auctionsLoading}
                  className="hover:text-text disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  next →
                </button>
              </div>
            }
          />
        </>
      ) : (
        <>
          <DataTable
            columns={bidColumns}
            data={bids?.items ?? []}
            rowKey={(b, i) => b.id || String(i)}
            loading={bidsLoading}
            emptyMessage="No bids placed yet."
          />
          <PageFooter
            left={
              <span>
                Page <b className="text-text-2">{bidsPage}</b> of <b className="text-text-2">{bids?.pages?.lastPage ?? 1}</b> ·
                total <b className="text-text-2">{bids?.pages?.total ?? bids?.items.length ?? 0}</b>
              </span>
            }
            right={
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => loadBids(Math.max(1, bidsPage - 1))}
                  disabled={bidsPage <= 1 || bidsLoading}
                  className="hover:text-text disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  ← prev
                </button>
                <button
                  type="button"
                  onClick={() => loadBids(bidsPage + 1)}
                  disabled={(bids?.pages?.lastPage ?? 1) <= bidsPage || bidsLoading}
                  className="hover:text-text disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  next →
                </button>
              </div>
            }
          />
        </>
      )}
    </>
  );
};

function isUrgent(sale: NCAftermarketSale): boolean {
  if (!sale.endDate) return false;
  const ms = sale.endDate.getTime() - Date.now();
  return ms > 0 && ms < URGENT_THRESHOLD_MS;
}

function formatRemaining(end: Date): string {
  const ms = end.getTime() - Date.now();
  if (ms <= 0) return 'ended';
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  if (days > 0) return `${days}d ${hours.toString().padStart(2, '0')}h`;
  if (hours > 0) return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
  const seconds = Math.floor(totalSec % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

const auctionColumns: Column<NCAftermarketSale>[] = [
  {
    key: 'name',
    title: 'Domain',
    render: (s) => <DomainName name={s.name} />,
  },
  {
    key: 'status',
    title: 'Status',
    render: (s) => {
      if (!s.status) return <span className="text-text-3">—</span>;
      const lower = s.status.toLowerCase();
      const tone =
        lower.includes('live') || lower.includes('auction') ? 'success' :
        lower.includes('buy') || lower.includes('fixed') ? 'warn' :
        'neutral';
      return <StatusTag tone={tone}>{s.status}</StatusTag>;
    },
  },
  {
    key: 'type',
    title: 'Type',
    render: (s) => <span className="text-text-3 text-[12.5px]">{s.auctionType || s.saleType || '—'}</span>,
  },
  {
    key: 'price',
    title: 'Price',
    align: 'right',
    render: (s) => <Num value={s.price ?? null} format="currency" />,
  },
  {
    key: 'minBid',
    title: 'Min Bid',
    align: 'right',
    render: (s) => <Num value={s.minBid ?? null} format="currency" tone="muted" />,
  },
  {
    key: 'bidCount',
    title: 'Bids',
    align: 'right',
    render: (s) => <Num value={s.bidCount ?? 0} format="int" tone={s.bidCount ? 'default' : 'muted'} />,
  },
  {
    key: 'estibot',
    title: 'Estibot',
    align: 'right',
    render: (s) => <Num value={s.estibotValue ?? null} format="currency" />,
  },
  {
    key: 'ratio',
    title: 'Ratio',
    align: 'right',
    render: (s) => {
      if (s.estibotValue == null || s.price == null || s.price === 0) return <Num value={null} />;
      return <Num value={s.estibotValue / s.price} format="ratio" tone="up" />;
    },
  },
  {
    key: 'ends',
    title: 'Ends',
    align: 'right',
    render: (s) => {
      if (!s.endDate) return <Num value={null} />;
      const urgent = isUrgent(s);
      return (
        <span
          title={moment(s.endDate).format('LLL')}
          className={`font-mono text-[12.5px] tabular-nums ${urgent ? 'text-danger font-semibold' : 'text-text'}`}
        >
          {formatRemaining(s.endDate)}
        </span>
      );
    },
  },
];

const bidColumns: Column<NCAftermarketBid>[] = [
  {
    key: 'name',
    title: 'Domain',
    render: (b) => <DomainName name={b.name} />,
  },
  {
    key: 'amount',
    title: 'Bid',
    align: 'right',
    render: (b) => <Num value={b.amount ?? null} format="currency" />,
  },
  {
    key: 'maxAmount',
    title: 'Max',
    align: 'right',
    render: (b) => <Num value={b.maxAmount ?? null} format="currency" tone="muted" />,
  },
  {
    key: 'isProxy',
    title: 'Proxy',
    render: (b) => (
      <span className="text-text-3 text-[12.5px]">{b.isProxy ? 'Yes' : 'No'}</span>
    ),
  },
  {
    key: 'isLeading',
    title: 'Leading',
    render: (b) =>
      b.isLeadingBid ? <StatusTag tone="success">Leading</StatusTag> : <StatusTag tone="neutral">Outbid</StatusTag>,
  },
  {
    key: 'status',
    title: 'Status',
    render: (b) => <span className="text-text-3 text-[12.5px]">{b.status || '—'}</span>,
  },
  {
    key: 'created',
    title: 'Placed',
    align: 'right',
    render: (b) => (
      <span className="font-mono text-[12.5px] text-text-3 tabular-nums">
        {b.createdDate ? moment(b.createdDate).format('MMM D, HH:mm') : '—'}
      </span>
    ),
  },
];

interface FilterPopoverProps {
  label: string;
  value?: string;
  active?: boolean;
  content: (close: () => void) => React.ReactNode;
}

function FilterPopover({ label, value, active, content }: FilterPopoverProps) {
  const [open, setOpen] = useState(false);
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      content={<div className="p-1">{content(() => setOpen(false))}</div>}
    >
      <span>
        <FilterPill label={label} value={value || '—'} active={active} />
      </span>
    </Popover>
  );
}

interface FilterTextPopoverProps {
  label: string;
  value?: string;
  placeholder?: string;
  onChange: (v: string) => void;
}

function FilterTextPopover({ label, value, placeholder, onChange }: FilterTextPopoverProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value ?? '');

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      content={
        <div className="p-1 min-w-[180px]">
          <input
            type="text"
            value={draft}
            placeholder={placeholder}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onChange(draft);
                setOpen(false);
              }
              if (e.key === 'Escape') setOpen(false);
            }}
            className="w-full px-2 py-1.5 bg-panel border border-border-strong rounded-md text-[12.5px] text-text outline-none focus:border-accent"
          />
          <div className="flex justify-end gap-1.5 mt-2">
            <button
              type="button"
              onClick={() => {
                setDraft('');
                onChange('');
                setOpen(false);
              }}
              className="text-[11px] text-text-3 hover:text-text px-2 py-1"
            >
              clear
            </button>
            <button
              type="button"
              onClick={() => {
                onChange(draft);
                setOpen(false);
              }}
              className="text-[11px] text-accent font-semibold hover:opacity-80 px-2 py-1"
            >
              apply
            </button>
          </div>
        </div>
      }
    >
      <span>
        <FilterPill label={label} value={value || '—'} active={!!value} />
      </span>
    </Popover>
  );
}

export default NCAftermarket;
